import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Radar } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/29057946/pexels-photo-29057946.jpeg?auto=compress&cs=tinysrgb&w=1920";

const RevealLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%", rotate: 2 }}
      animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.25]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-end overflow-hidden grain"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Heavy cargo trucks on an Indian highway"
          className="w-full h-full object-cover scale-110"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D1117]/80 via-[#0D1117]/55 to-[#0D1117]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D1117]/85 via-transparent to-transparent" />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="relative max-w-7xl mx-auto w-full px-5 sm:px-8 pt-36 pb-10"
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          data-testid="hero-badge"
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-900/80 border border-slate-700 text-amber-400 mono text-[10px] sm:text-xs uppercase tracking-[0.25em] font-semibold"
        >
          <span className="w-1.5 h-1.5 bg-amber-500 animate-pulse" aria-hidden="true" />
          FTL · ODC · Trailers · Warehousing — Pan-India
        </motion.div>

        <h1
          data-testid="hero-title"
          className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-[0.98] text-slate-100"
        >
          <RevealLine delay={0.3}>Pan-India freight,</RevealLine>
          <RevealLine delay={0.42}>moved with</RevealLine>
          <RevealLine delay={0.54}>
            <span className="text-amber-500">discipline.</span>
          </RevealLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-6 max-w-xl text-base text-slate-300 leading-relaxed"
        >
          Full truck loads, over-dimensional consignments, heavy trailers and
          warehousing — executed lane after lane with GPS-tracked, on-time
          discipline across the country.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <a
            href="#track"
            data-testid="hero-cta-track"
            className="group mono inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold bg-amber-500 text-[#0D1117] px-7 py-4 hover:bg-amber-400 transition-colors duration-200"
          >
            <Radar className="w-4 h-4" />
            Track Shipment
            <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mono mt-14 pt-6 border-t border-slate-800/80 flex flex-wrap gap-x-8 gap-y-2 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-slate-500"
        >
          <span>250+ lanes served</span>
          <span>28 states &amp; UTs</span>
          <span>GPS-tracked fleet</span>
          <span>On-time discipline</span>
        </motion.div>
      </motion.div>
    </section>
  );
};
